// =============================================================================
// MeuDriver — Student Payment History
// =============================================================================

import { Link } from 'react-router-dom'
import { CreditCard, ArrowLeft, CheckCircle, Clock, XCircle, ExternalLink, Download } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'
import { downloadCsv, formatDateTimeBR, formatBRL } from '@/lib/csv'
import { toast } from 'sonner'

// ─── Types ────────────────────────────────────────────────────────────────────

interface PaymentRow {
  id: string
  amount: number
  status: string
  provider: string | null
  paid_at: string | null
  booking: {
    id: string
    scheduled_at: string
    instructor: {
      profile: { full_name: string | null } | null
    } | null
  } | null
}

interface BookingWithPayments {
  id: string
  scheduled_at: string
  instructor: {
    profile: { full_name: string | null } | null
  } | null
  payments: Array<{
    id: string
    amount: number
    status: string
    provider: string | null
    paid_at: string | null
  }>
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusIcon(status: string) {
  if (status === 'paid') return <CheckCircle className="h-4 w-4 text-green-500" />
  if (status === 'pending') return <Clock className="h-4 w-4 text-amber-500" />
  return <XCircle className="h-4 w-4 text-red-400" />
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    paid: 'Aprovado',
    pending: 'Pendente',
    failed: 'Falhou',
    refunded: 'Reembolsado',
  }
  return map[status] ?? status
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'paid') return 'default'
  if (status === 'pending') return 'secondary'
  return 'destructive'
}

function providerLabel(provider: string | null): string {
  const map: Record<string, string> = {
    stripe: 'Stripe',
    mercadopago: 'Mercado Pago',
  }
  return provider ? (map[provider] ?? provider) : '—'
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function StudentPayments() {
  const { user } = useAuth()

  const { data: payments, isLoading } = useQuery<PaymentRow[]>({
    queryKey: ['student-payments', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id, scheduled_at,
          instructor:instructors!bookings_instructor_id_fkey(
            profile:profiles(full_name)
          ),
          payments!inner(
            id, amount, status, provider, paid_at
          )
        `)
        .eq('student_id', user!.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      const rows: PaymentRow[] = []
      for (const booking of (data ?? []) as unknown as BookingWithPayments[]) {
        for (const payment of booking.payments) {
          rows.push({
            id: payment.id,
            amount: payment.amount,
            status: payment.status,
            provider: payment.provider,
            paid_at: payment.paid_at,
            booking: {
              id: booking.id,
              scheduled_at: booking.scheduled_at,
              instructor: booking.instructor,
            },
          })
        }
      }

      rows.sort((a, b) => {
        if (!a.paid_at && !b.paid_at) return 0
        if (!a.paid_at) return 1
        if (!b.paid_at) return -1
        return b.paid_at.localeCompare(a.paid_at)
      })

      return rows
    },
    staleTime: 1000 * 60,
  })

  const totalSpent =
    payments?.filter((p) => p.status === 'paid').reduce((s, p) => s + p.amount, 0) ?? 0

  function handleExportCsv() {
    if (!payments || payments.length === 0) {
      toast.info('Sem pagamentos para exportar.')
      return
    }
    const today = new Date().toISOString().slice(0, 10)
    downloadCsv(
      `meudriver-pagamentos-${today}.csv`,
      payments,
      [
        { key: 'paid_at', label: 'Data do pagamento', map: (v) => formatDateTimeBR(v) },
        {
          key: 'scheduled_at',
          label: 'Data da aula',
          map: (_v, row) => formatDateTimeBR(row.booking?.scheduled_at),
        },
        {
          key: 'instructor',
          label: 'Instrutor',
          map: (_v, row) => row.booking?.instructor?.profile?.full_name ?? '—',
        },
        { key: 'provider', label: 'Meio de pagamento', map: (v) => providerLabel(v as string | null) },
        { key: 'amount', label: 'Valor', map: (v) => formatBRL(v) },
        { key: 'status', label: 'Status', map: (v) => statusLabel(v as string) },
      ],
    )
    toast.success('Extrato exportado.')
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="shrink-0">
          <Link to="/aluno/dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Histórico de pagamentos
          </h1>
          {payments && payments.length > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {payments.length} transaç{payments.length > 1 ? 'ões' : 'ão'}
            </p>
          )}
        </div>
        {totalSpent > 0 && (
          <div className="shrink-0 text-right">
            <p className="text-xl font-bold text-gray-900">{formatCurrency(totalSpent)}</p>
            <p className="text-xs text-muted-foreground">total investido</p>
          </div>
        )}
        {payments && payments.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="shrink-0 gap-1"
            title="Exportar histórico em CSV"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </Button>
        )}
      </div>

      {/* Content */}
      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : !payments || payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 mb-4">
              <CreditCard className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-base font-semibold text-gray-700">Nenhum pagamento ainda</p>
            <p className="text-sm text-muted-foreground mt-1">
              Seus pagamentos de aulas aparecerão aqui.
            </p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/instrutores">Encontrar instrutores</Link>
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {payments.map((p) => {
              const instructorName = p.booking?.instructor?.profile?.full_name ?? 'Instrutor'
              const lessonDate = p.booking?.scheduled_at
                ? format(parseISO(p.booking.scheduled_at), "d 'de' MMM 'de' yyyy", { locale: ptBR })
                : '—'
              const paidDate = p.paid_at
                ? format(parseISO(p.paid_at), "d/MM/yy 'às' HH:mm", { locale: ptBR })
                : 'Pendente'

              return (
                <div
                  key={p.id}
                  className="flex items-start gap-4 px-4 sm:px-6 py-4 hover:bg-muted/20 transition-colors"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100">
                    {statusIcon(p.status)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          Aula com {instructorName}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{lessonDate}</p>
                        <p className="text-xs text-muted-foreground">
                          {providerLabel(p.provider)} · {paidDate}
                        </p>
                      </div>
                      <div className="shrink-0 text-right space-y-1">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(p.amount)}</p>
                        <Badge
                          variant={statusVariant(p.status)}
                          className="text-[10px] px-2 py-0.5"
                        >
                          {statusLabel(p.status)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {p.booking?.id && (
                    <Link
                      to={`/aluno/aulas/${p.booking.id}`}
                      className="shrink-0 text-muted-foreground hover:text-primary transition-colors mt-1"
                      title="Ver aula"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
